import type {
  MapWorkbenchAdvancedRuleDefinition,
  MapWorkbenchAdvancedRuleEditorPanelProps,
  MapWorkbenchAdvancedRuleLogicNode,
  MapWorkbenchAdvancedRuleValidationMessage,
  RuleFieldOption,
  RuleOperatorOption,
} from '@workspace/silk-ui';

import {
  normalizeAdvancedRuleColor,
} from '../map-layer-rules/colors';
import type { LayerRuleBuilderState } from '../map-layer-rules';
import { createGroup, type StyleRuleLogic } from '../map-style-workbench/builderLogic';

export type AdvancedRuleWorkbenchFieldOption = {
  id: string;
  label: string;
  value_type: 'string' | 'number' | 'boolean' | 'date';
};

export const ADVANCED_RULE_OPERATOR_OPTIONS: RuleOperatorOption[] = [
  { id: 'eq', label: 'Equals', requires_value: true, supported_value_types: ['string', 'number', 'boolean', 'date'] },
  { id: 'neq', label: 'Does not equal', requires_value: true, supported_value_types: ['string', 'number', 'boolean', 'date'] },
  { id: 'contains', label: 'Contains', requires_value: true, supported_value_types: ['string'] },
  { id: 'exists', label: 'Exists', requires_value: false, supported_value_types: ['string', 'number', 'boolean', 'date'] },
  { id: 'gt', label: 'Greater than', requires_value: true, supported_value_types: ['number', 'date'] },
  { id: 'gte', label: 'Greater than or equal', requires_value: true, supported_value_types: ['number', 'date'] },
  { id: 'lt', label: 'Less than', requires_value: true, supported_value_types: ['number', 'date'] },
  { id: 'lte', label: 'Less than or equal', requires_value: true, supported_value_types: ['number', 'date'] },
  { id: 'in', label: 'In list', requires_value: true, supported_value_types: ['string', 'number'] },
  { id: 'match_everything', label: 'Match everything', requires_value: false, supported_value_types: ['string', 'number', 'boolean', 'date'] },
];

export const buildAdvancedRuleFieldOptions = (fields: AdvancedRuleWorkbenchFieldOption[]): RuleFieldOption[] =>
  fields.map(field => ({
    id: field.id,
    label: field.label,
    value_type: field.value_type,
  }));

export const ensureAdvancedRuleGroup = (logic: StyleRuleLogic): MapWorkbenchAdvancedRuleDefinition['logic'] =>
  logic.type === 'group'
    ? {
        id: logic.id,
        kind: 'group',
        combinator: logic.operator === 'OR' ? 'or' : 'and',
        children: logic.children.map(convertStyleRuleLogicToAdvancedRuleLogic),
      }
    : {
        id: createGroup().id,
        kind: 'group',
        combinator: 'and',
        children: [convertStyleRuleLogicToAdvancedRuleLogic(logic)],
      };

function convertStyleRuleLogicToAdvancedRuleLogic(logic: StyleRuleLogic): MapWorkbenchAdvancedRuleLogicNode {
  if (logic.type === 'condition') {
    const values = Array.isArray(logic.values) ? logic.values : [];
    return {
      id: logic.id,
      kind: 'condition',
      field: logic.fieldId,
      operator: logic.operatorId,
      value: values.length <= 1 ? (values[0] ?? null) : values,
    };
  }
  return {
    id: logic.id,
    kind: 'group',
    combinator: logic.operator === 'OR' ? 'or' : 'and',
    children: logic.children.map(convertStyleRuleLogicToAdvancedRuleLogic),
  };
}

export const convertAdvancedRuleLogicToStyleRuleLogic = (
  logic: MapWorkbenchAdvancedRuleLogicNode | StyleRuleLogic | null | undefined,
): StyleRuleLogic => {
  if (!logic || typeof logic !== 'object') {
    return createGroup();
  }

  if ('type' in logic) {
    if (logic.type === 'condition') {
      return {
        id: logic.id,
        type: 'condition',
        fieldId: logic.fieldId,
        operatorId: logic.operatorId,
        values: Array.isArray(logic.values) ? logic.values.map(entry => String(entry)) : [],
      };
    }
    return {
      id: logic.id,
      type: 'group',
      operator: logic.operator === 'OR' ? 'OR' : 'AND',
      children: Array.isArray(logic.children)
        ? logic.children.map(child => convertAdvancedRuleLogicToStyleRuleLogic(child))
        : [],
    };
  }

  if (logic.kind === 'condition') {
    const rawValue = logic.value;
    return {
      id: logic.id,
      type: 'condition',
      fieldId: logic.field,
      operatorId: logic.operator,
      values: rawValue == null ? [] : Array.isArray(rawValue) ? rawValue.map(entry => String(entry)) : [String(rawValue)],
    };
  }
  return {
    id: logic.id,
    type: 'group',
    operator: logic.combinator === 'or' ? 'OR' : 'AND',
    children: Array.isArray(logic.children)
      ? logic.children.map(child => convertAdvancedRuleLogicToStyleRuleLogic(child))
      : [],
  };
};

export const buildAdvancedRuleValidation = (
  rules: LayerRuleBuilderState['layers'][number]['rules'],
  fields: AdvancedRuleWorkbenchFieldOption[],
): MapWorkbenchAdvancedRuleValidationMessage[] => {
  const availableFieldIds = new Set(fields.map(field => field.id));
  return rules.flatMap((rule, index) => {
    const messages: MapWorkbenchAdvancedRuleValidationMessage[] = [];
    const visit = (logic: StyleRuleLogic) => {
      if (logic.type === 'condition') {
        if (logic.operatorId === 'match_everything') {
          return;
        }
        if (!logic.fieldId || !availableFieldIds.has(logic.fieldId)) {
          messages.push({
            id: `${rule.id}:${logic.id}:field`,
            level: 'error',
            message: 'Select a valid field for each condition.',
            ruleId: rule.id,
            nodeId: logic.id,
          });
          return;
        }
        if (!logic.operatorId) {
          messages.push({
            id: `${rule.id}:${logic.id}:operator`,
            level: 'error',
            message: 'Select an operator for each condition.',
            ruleId: rule.id,
            nodeId: logic.id,
          });
          return;
        }
        const field = fields.find(entry => entry.id === logic.fieldId);
        const operator = ADVANCED_RULE_OPERATOR_OPTIONS.find(entry => entry.id === logic.operatorId);
        if (operator?.requires_value !== false) {
          const values = logic.values ?? [];
          if (values.length === 0 || values.every(value => String(value).trim().length === 0)) {
            messages.push({
              id: `${rule.id}:${logic.id}:value`,
              level: 'error',
              message: field?.value_type === 'boolean'
                ? 'Select true or false for each boolean condition.'
                : 'Enter a value for each condition.',
              ruleId: rule.id,
              nodeId: logic.id,
            });
          }
        }
        return;
      }
      if (logic.children.length === 0) {
        messages.push({
          id: `${rule.id}:${logic.id}:children`,
          level: 'warning',
          message: 'Groups should include at least one condition.',
          ruleId: rule.id,
          nodeId: logic.id,
        });
      }
      logic.children.forEach(visit);
    };
    visit(rule.logic);
    if (!rule.label.trim()) {
      messages.push({
        id: `${rule.id}:label`,
        level: 'warning',
        message: 'Give each rule a short label so it is easy to scan later.',
        ruleId: rule.id,
      });
    }
    const hasCatchAll = rule.logic.type === 'group'
      ? rule.logic.children.some(child => child.type === 'condition' && child.operatorId === 'match_everything')
      : rule.logic.operatorId === 'match_everything';
    if (hasCatchAll && index < rules.length - 1) {
      messages.push({
        id: `${rule.id}:catch-all-order`,
        level: 'warning',
        message: 'Match Everything should usually be the last rule, or later rules will never apply.',
        ruleId: rule.id,
      });
    }
    return messages;
  });
};

type AdvancedRuleEditorControllerArgs = {
  ruleLayer?: LayerRuleBuilderState['layers'][number] | null;
  iconPreviewByToken?: Record<string, { previewUrl?: string | null; sdf?: boolean | null }>;
  selectedRuleId?: string | null;
  onSelectedRuleChange?: (id: string) => void;
  onAddRule: MapWorkbenchAdvancedRuleEditorPanelProps['onAddRule'];
  onDuplicateRule: MapWorkbenchAdvancedRuleEditorPanelProps['onDuplicateRule'];
  onDeleteRule: MapWorkbenchAdvancedRuleEditorPanelProps['onDeleteRule'];
  onRuleChange: MapWorkbenchAdvancedRuleEditorPanelProps['onRuleChange'];
  onEditStyle?: MapWorkbenchAdvancedRuleEditorPanelProps['onEditStyle'];
  fields: AdvancedRuleWorkbenchFieldOption[];
  legendHeading: string;
  legendLabel: string;
  onLegendHeadingChange: (value: string) => void;
  onLegendLabelChange: (value: string) => void;
  canEditLegend: boolean;
  footerMessage?: string | null;
  canApply?: boolean;
  applyDisabled?: boolean;
  applyLabel?: string;
  onApply?: () => void;
  canSave?: boolean;
  saveDisabled?: boolean;
  saveLabel?: string;
  onSave?: () => void;
  readOnly?: boolean;
};

export function buildAdvancedRuleEditorPanelProps({
  ruleLayer,
  iconPreviewByToken,
  selectedRuleId,
  onSelectedRuleChange,
  onAddRule,
  onDuplicateRule,
  onDeleteRule,
  onRuleChange,
  onEditStyle,
  fields,
  legendHeading,
  legendLabel,
  onLegendHeadingChange,
  onLegendLabelChange,
  canEditLegend,
  footerMessage,
  canApply,
  applyDisabled,
  applyLabel,
  onApply,
  canSave,
  saveDisabled,
  saveLabel,
  onSave,
  readOnly,
}: AdvancedRuleEditorControllerArgs): MapWorkbenchAdvancedRuleEditorPanelProps {
  const panelFields = buildAdvancedRuleFieldOptions(fields);
  const rules: MapWorkbenchAdvancedRuleDefinition[] = (ruleLayer?.rules ?? []).map(rule => {
    const iconToken =
      rule.layout && typeof rule.layout === 'object' && typeof (rule.layout as Record<string, unknown>)['icon-image'] === 'string'
        ? (((rule.layout as Record<string, unknown>)['icon-image'] as string).trim() || null)
        : null;
    const iconPreview = iconToken ? iconPreviewByToken?.[iconToken] : null;
    return {
      id: rule.id,
      label: rule.label,
      enabled: rule.enabled,
      color: normalizeAdvancedRuleColor(rule.color),
      logic: ensureAdvancedRuleGroup(rule.logic),
      representation: rule.representation ?? null,
      paint: rule.paint ?? null,
      layout: rule.layout ?? null,
      iconToken,
      iconPreviewUrl: iconPreview?.previewUrl ?? null,
      iconSdf: iconPreview?.sdf ?? false,
    };
  });

  return {
    rules,
    iconPreviewByToken,
    selectedRuleId,
    onSelectedRuleChange,
    onAddRule,
    onDuplicateRule,
    onDeleteRule,
    onRuleChange,
    onEditStyle,
    fields: panelFields,
    operators: ADVANCED_RULE_OPERATOR_OPTIONS,
    validation: buildAdvancedRuleValidation(ruleLayer?.rules ?? [], fields),
    legendHeading,
    legendLabel,
    onLegendHeadingChange,
    onLegendLabelChange,
    canEditLegend,
    footerMessage,
    canApply,
    applyDisabled,
    applyLabel,
    onApply,
    canSave,
    saveDisabled,
    saveLabel,
    onSave,
    readOnly,
  };
}

export type { MapWorkbenchAdvancedRuleDefinition };
